import { getT } from '@/i18n';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPersonBiking, faPersonRunning, faPersonWalking } from '@fortawesome/free-solid-svg-icons';
import { Button, EmptyState, OptionSwitch } from '@/components/ui';
import { ActivityCard } from '@/components/ActivityCard';
import { useActivities } from '@/hooks/useActivities';
import { currentPosition } from '@/lib/currentPosition';
import type { FeedActivity, FeedResult, FeedScope } from '@/lib/api';
import { useT } from '@/i18n/react';
import './feed.css';

/**
 * Aktivitás-feed, nézetekkel.
 *
 *   Mindenki → Globális — minden aktivitás, időrendben
 *   Mindenki → Helyi    — csak a közelben történtek, állítható sugárral
 *   Követed              — akiket követsz (még nincs követési gráf)
 *
 * A választás megmarad: aki a helyi nézetet szereti, annak minden megnyitáskor
 * átkattintani fölösleges lépés.
 */

const TAB_KEY = 'grundo.feed.tab';
const RADIUS_KEY = 'grundo.feed.radiusKm';
const DATE_KEY = 'grundo.feed.date';
const TYPES_KEY = 'grundo.feed.types';

type Tab = 'global' | 'following';
type GlobalView = 'world' | 'local';
type DatePreset = 'today' | 'week' | 'month' | 'always' | 'custom';
type ActivityType = FeedActivity['type'];

const DATE_PRESETS: readonly DatePreset[] = ['today', 'week', 'month', 'always', 'custom'];

/**
 * A mozgásforma-szűrő.
 *
 * ⚠️ TÖBBET IS BE LEHET KAPCSOLNI, és alapból mind a három BE van. Egy
 * hárompozíciós, egymást kizáró kapcsoló azt jelentené, hogy vegyes feedet
 * SOHA nem lehet nézni — pedig épp az az alapállapot, amit a felhasználók
 * túlnyomó része akar. Így viszont a szűrés szűkítés: leveszed, ami nem
 * érdekel.
 *
 * ⚠️ AZ UTOLSÓ BEKAPCSOLTAT NEM LEHET LEVENNI. „Egyik mozgásforma sem" nem
 * hasznos állapot — üres listát adna, és a felhasználó azt hinné, elromlott
 * valami. A koppintás ilyenkor egyszerűen nem csinál semmit.
 */
const TYPE_ICONS: Readonly<Record<ActivityType, IconDefinition>> = {
  walk: faPersonWalking,
  run: faPersonRunning,
  ride: faPersonBiking,
};

const ALL_TYPES: readonly ActivityType[] = ['walk', 'run', 'ride'];

/** A választható sugarak. Nem szabad szöveges beviteli mező: futás után, egy
 *  kézzel senki nem akar számot gépelni. */
const RADII = [1, 5, 10, 25, 50] as const;

export function Feed() {
  const navigate = useNavigate();
  const t = useT();
  const dateOptions = DATE_PRESETS.map((value) => ({ value, label: t.feed.dates[value] }));
  const typeOptions: readonly { value: ActivityType; label: string; icon: IconDefinition }[] = [
    { value: 'walk', label: t.activity.types.walk, icon: TYPE_ICONS.walk },
    { value: 'run', label: t.activity.types.run, icon: TYPE_ICONS.run },
    { value: 'ride', label: t.activity.types.ride, icon: TYPE_ICONS.ride },
  ];

  const [tab, setTab] = useState<Tab>(() => read(TAB_KEY) === 'following' ? 'following' : 'global');
  const [view, setView] = useState<GlobalView>('world');
  const [radiusKm, setRadiusKm] = useState<number>(() => Number(read(RADIUS_KEY)) || 10);
  const [datePreset, setDatePreset] = useState<DatePreset>(() => readDatePreset());
  const [types, setTypes] = useState<readonly ActivityType[]>(() => readTypes());
  const [customFrom, setCustomFrom] = useState(() => localDateValue(new Date()));
  const [customTo, setCustomTo] = useState(() => localDateValue(new Date()));
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const dateSelectRef = useRef<HTMLDivElement | null>(null);
  const dateTriggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!dateMenuOpen) return;

    const closeFromOutside = (event: PointerEvent) => {
      if (!dateSelectRef.current?.contains(event.target as Node)) setDateMenuOpen(false);
    };
    const closeFromKeyboard = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDateMenuOpen(false);
      dateTriggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', closeFromOutside);
    window.addEventListener('keydown', closeFromKeyboard);
    return () => {
      document.removeEventListener('pointerdown', closeFromOutside);
      window.removeEventListener('keydown', closeFromKeyboard);
    };
  }, [dateMenuOpen]);

  /**
   * A helyi nézethez a saját pozíció kell.
   *
   * Kis pontossággal, akár gyorsítótárból kérjük: nem mérünk vele, csak azt
   * döntjük el, mi számít „közelinek". Egy 10 km-es sugárnál a néhány száz
   * méteres tévedés nem számít, viszont a pontos fix megvárása másodpercekbe
   * kerülne, és külön engedélykérést villantana fel.
   */
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [positionDenied, setPositionDenied] = useState(false);

  useEffect(() => {
    if (tab !== 'global' || view !== 'local' || position !== null) return;
    /* ⚠️ Natívban SOHA nem a `navigator.geolocation` — lásd `currentPosition`. */
    void currentPosition({ purpose: getT().remainingUI.feed })
      .then((fix) => setPosition({ lat: fix.lat, lng: fix.lng }))
      .catch(() => setPositionDenied(true));
  }, [tab, view, position]);

  const scope: FeedScope =
    tab === 'following'
      ? 'following'
      : view === 'local'
        ? 'local'
        : 'world';
  // A „most" időbélyeg egy renderen belül stabil marad. Korábban minden
  // render új `dateTo` értéket adott, ami új lekérést, majd újabb rendert
  // indított — ettől vibrált a feed minden nem-MINDIG szűrőnél.
  const dateRange = useMemo(
    () => feedDateRange(datePreset, customFrom, customTo),
    [datePreset, customFrom, customTo],
  );

  // A helyi nézet pozíció nélkül nem kérdezhető le — addig nem indítunk kérést.
  const awaitingPosition = scope === 'local' && position === null;
  const { result, loading, error, reload, loadMore, hasMore, loadingMore } = useActivities(
    awaitingPosition || dateRange === null
      ? null
      : {
          scope,
          ...dateRange,
          ...(scope === 'local' ? { ...position!, radiusKm } : {}),
        },
  );

  /**
   * A mozgásforma-szűrés a BETÖLTÖTT lapokon fut, nem a szerveren.
   *
   * A `type` szerinti szerveroldali szűréshez `where('type','in',…)` kellene
   * az `orderBy('endedAt')` mellé, ami újabb összetett indexet jelentene.
   *
   * ⚠️ EMIATT EGY LAP TELJESEN ÜRESRE SZŰRŐDHET. A „Továbbiak betöltése"
   * gomb ilyenkor is megmarad — a következő lapon jöhet illeszkedő sor.
   */
  const visibleResult = useMemo<FeedResult | null>(() => {
    if (result === null) return null;
    if (types.length === ALL_TYPES.length) return result;
    return {
      ...result,
      activities: result.activities.filter((item) => types.includes(item.type)),
    };
  }, [result, types]);

  function chooseTab(next: Tab) {
    setTab(next);
    write(TAB_KEY, next);
  }

  function chooseRadius(next: number) {
    setRadiusKm(next);
    write(RADIUS_KEY, String(next));
  }

  function chooseDate(next: DatePreset) {
    setDatePreset(next);
    write(DATE_KEY, next);
    setDateMenuOpen(false);
    dateTriggerRef.current?.focus();
  }

  function toggleType(value: ActivityType) {
    setTypes((current) => {
      const on = current.includes(value);
      // Az utolsó bekapcsolt nem vehető le — lásd a mozgásforma-szűrő leírását.
      if (on && current.length === 1) return current;
      const next = on ? current.filter((item) => item !== value) : [...current, value];
      write(TYPES_KEY, next.join(','));
      return next;
    });
  }

  function focusDateOption(direction: 1 | -1, event: KeyboardEvent<HTMLButtonElement>) {
    event.preventDefault();
    const options = Array.from(
      dateSelectRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [],
    );
    const current = options.indexOf(event.currentTarget);
    options[(current + direction + options.length) % options.length]?.focus();
  }

  return (
    <div className="feed">
      <OptionSwitch
        label={t.feed.viewLabel}
        value={tab}
        onChange={chooseTab}
        options={[
          { value: 'global', label: t.feed.everyone },
          { value: 'following', label: t.feed.following },
        ]}
      />

      <div className="feed__filterbar">
        {tab === 'global' ? (
          <div className="feed__geo" role="group" aria-label={t.feed.areaFilter}>
            <button
              type="button"
              className={view === 'world' ? 'feed__geo-btn feed__geo-btn--on' : 'feed__geo-btn'}
              onClick={() => setView('world')}
            >
              {t.feed.global}
            </button>
            <button
              type="button"
              className={view === 'local' ? 'feed__geo-btn feed__geo-btn--on' : 'feed__geo-btn'}
              onClick={() => setView('local')}
            >
              {t.feed.local}
            </button>
          </div>
        ) : null}

        {/* A mozgásforma-választó a jobb oldali szabad hely ~60%-át kapja, a
            maradék két egyenlő térközként kerül a két oldalára (Geri, #71). */}
        {tab === 'global' ? <span className="feed__filter-gap" aria-hidden="true" /> : null}
        <div className="feed__geo feed__geo--types" role="group" aria-label={t.feed.typeFilter}>
          {typeOptions.map((option) => {
            const on = types.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={on}
                aria-label={option.label}
                title={option.label}
                className={on ? 'feed__geo-btn feed__geo-btn--on' : 'feed__geo-btn'}
                onClick={() => toggleType(option.value)}
              >
                <FontAwesomeIcon icon={option.icon} aria-hidden="true" />
              </button>
            );
          })}
        </div>
        <span className="feed__filter-gap" aria-hidden="true" />

        <div className="feed__date-select" ref={dateSelectRef}>
          <button
            ref={dateTriggerRef}
            type="button"
            className="feed__date-trigger"
            aria-label={t.feed.dateFilter}
            aria-haspopup="listbox"
            aria-expanded={dateMenuOpen}
            onClick={() => setDateMenuOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
              event.preventDefault();
              setDateMenuOpen(true);
              window.requestAnimationFrame(() => {
                dateSelectRef.current
                  ?.querySelector<HTMLButtonElement>('[role="option"][aria-selected="true"]')
                  ?.focus();
              });
            }}
          >
            <span>{dateOptions.find((option) => option.value === datePreset)?.label}</span>
            <ChevronIcon open={dateMenuOpen} />
          </button>

          {dateMenuOpen ? (
            <div className="feed__date-menu" role="listbox" aria-label={t.feed.period}>
              {dateOptions.map((option) => {
                const selected = option.value === datePreset;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`feed__date-option${selected ? ' feed__date-option--on' : ''}`}
                    onClick={() => chooseDate(option.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowDown') focusDateOption(1, event);
                      else if (event.key === 'ArrowUp') focusDateOption(-1, event);
                    }}
                  >
                    <span>{option.label}</span>
                    {selected ? <CheckIcon /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      {datePreset === 'custom' ? (
        <div className="feed__custom-dates">
          <label>
            <span>{t.feed.dates.from}</span>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </label>
          <label>
            <span>{t.feed.dates.until}</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </label>
        </div>
      ) : null}

      {tab === 'global' && view === 'local' ? (
        <div className="feed__radius">
          <span className="feed__radius-label">{t.feed.radiusLabel}</span>
          <div className="feed__radius-options">
            {RADII.map((km) => (
              <button
                key={km}
                type="button"
                className={`feed__radius-chip${km === radiusKm ? ' feed__radius-chip--on' : ''}`}
                onClick={() => chooseRadius(km)}
              >
                {km} km
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {dateRange === null ? (
        <div className="card" role="alert">{t.feed.invalidDateRange}</div>
      ) : positionDenied && scope === 'local' ? (
        <div className="card">
          {t.feed.locationRequired}
        </div>
      ) : awaitingPosition ? (
        <div className="card">{t.feed.locating}</div>
      ) : /*
           A SZŰRŐ okozta üresség NEM ugyanaz, mint hogy nincs aktivitás. Az
           általános „Nincs mit mutatni" itt félrevezetne: a felhasználó azt
           hinné, a környéken nem mozgott senki, pedig csak levette a
           mozgásformát, amiben mozogtak.
         */
      result !== null && result.activities.length > 0 && visibleResult?.activities.length === 0 ? (
        <div className="card">
          {t.feed.filteredEmpty}
        </div>
      ) : (
        <ActivityList
          scope={scope}
          result={visibleResult}
          loading={loading}
          error={error}
          radiusKm={radiusKm}
          onRetry={reload}
          onStart={() => navigate('/rogzites')}
        />
      )}
      <ActivityPagination hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMore} />
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`feed__date-chevron${open ? ' feed__date-chevron--open' : ''}`}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}

export interface ActivityListProps {
  scope: FeedScope;
  result: FeedResult | null;
  loading: boolean;
  error: string;
  radiusKm?: number;
  onRetry: () => void;
  /** Csak a `mine` nézet üres állapotában van rá szükség. */
  onStart?: () => void;
}

/**
 * A lista maga — a profil is ezt használja, saját lekérdezéssel.
 *
 * Külön exportált, mert a profil UGYANABBÓL a betöltésből építi a heti
 * oszlopdiagramot és az összegzőket is.
 *
 * ⚠️ A `loading` már nem vált „Betöltés…"-re: a lapozó cache miatt a régi
 * lista marad kint a háttérfrissítés alatt, különben minden visszalépésnél
 * villogna a lista.
 */
export function ActivityList({
  scope,
  result,
  error,
  radiusKm = 10,
  onRetry,
  onStart,
}: ActivityListProps) {
  const t = useT();
  if (error && !result?.activities.length) {
    return (
      <div className="card" role="alert">
        {error}
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <Button size="sm" onClick={onRetry}>
            {t.feed.retry}
          </Button>
        </div>
      </div>
    );
  }

  if (result === null) return <div className="card">{t.feed.loading}</div>;

  if (result.activities.length === 0) {
    if (scope === 'mine') {
      return (
        <EmptyState
          title={t.feed.empty.mine.title}
          description={t.feed.empty.mine.description}
          action={<Button onClick={onStart}>{t.feed.empty.mine.action}</Button>}
        />
      );
    }
    /**
     * A követett feed üressége KÉT dolgot jelenthet: nem követsz még senkit,
     * vagy akiket követsz, azok nem mozogtak. A szerver ezt nem különbözteti
     * meg, ezért a szöveg mindkettőre értelmes marad — de a globális feedre
     * mutat, ahol embereket lehet találni.
     */
    if (scope === 'following') {
      return (
        <EmptyState
          title={t.feed.empty.following.title}
          description={t.feed.empty.following.description}
        />
      );
    }
    /**
     * Idegen profilon a globális feed szövege („Még senki nem rögzített
     * aktivitást") hazugság: nem mindenkiről van szó, hanem EGY emberről.
     */
    if (scope === 'user') {
      return (
        <EmptyState
          title={t.feed.empty.user.title}
          description={t.feed.empty.user.description}
        />
      );
    }
    return (
      <EmptyState
        title={scope === 'local' ? t.feed.empty.local.title : t.feed.empty.world.title}
        description={
          scope === 'local'
            ? t.feed.empty.local.description(radiusKm)
            : t.feed.empty.world.description
        }
      />
    );
  }

  return (
    <>
      <div className="feed__list">
        {result.activities.map((item: FeedActivity) => (
          <ActivityCard key={item.id} item={item} showAuthor={scope !== 'mine'} />
        ))}
      </div>
      {error ? <div className="card" role="alert">{error} <Button size="sm" onClick={onRetry}>{t.feed.retry}</Button></div> : null}
    </>
  );
}

export function ActivityPagination({ hasMore, loadingMore, onLoadMore }: {
  hasMore: boolean; loadingMore: boolean; onLoadMore: () => void;
}) {
  const t = useT();
  return hasMore ? <div style={{ marginTop: 'var(--sp-3)' }}><Button variant="secondary" block loading={loadingMore} onClick={onLoadMore}>
    {loadingMore ? t.feed.loading : t.feed.loadMore}
  </Button></div> : null;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* privát böngészés — a választás nem marad meg, de működik */
  }
}

function readTypes(): readonly ActivityType[] {
  const stored = (read(TYPES_KEY) ?? '')
    .split(',')
    .filter((value): value is ActivityType => (ALL_TYPES as readonly string[]).includes(value));
  // Üres vagy sérült érték esetén mind a három — lásd a mozgásforma-szűrő leírását.
  return stored.length > 0 ? stored : ALL_TYPES;
}

function readDatePreset(): DatePreset {
  const value = read(DATE_KEY);
  return value === 'today' || value === 'week' || value === 'month' || value === 'custom'
    ? value
    : 'always';
}

function feedDateRange(
  preset: DatePreset,
  customFrom: string,
  customTo: string,
): { dateFrom?: number; dateTo?: number } | null {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  let start: Date | null = null;

  if (preset === 'today') {
    start = startOfDay(now);
  } else if (preset === 'week') {
    start = startOfDay(now);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  } else if (preset === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (preset === 'custom') {
    const from = dateInputToLocal(customFrom);
    const to = dateInputToLocal(customTo);
    if (!from || !to || from.getTime() > to.getTime()) return null;
    to.setHours(23, 59, 59, 999);
    return { dateFrom: from.getTime(), dateTo: to.getTime() };
  }

  return start ? { dateFrom: start.getTime(), dateTo: end.getTime() } : {};
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function localDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateInputToLocal(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}
